<script lang="ts">
	import { onMount, onDestroy } from "svelte";
	import { fly, slide } from "svelte/transition";
	import { yyyy, goto } from "./store.svelte";
	import Fa from "svelte-fa";
	import {
		faChartLine,
		faCloudRain,
		faMountain,
		faExclamationTriangle,
		faUpload,
		faCog,
		faFileText,
		faCircleNotch,
		faRefresh,
		faEye,
		faWarning,
		faChevronDown,
		faChevronRight,
		faTrash
	} from "@fortawesome/free-solid-svg-icons";

	// State management
	let activeTab = $state("risk-overview");
	let loading = $state(true);
	let ready = $state(false);
	let error = $state(null);

	// Settings accordion state
	let expandedSections = $state({
		thresholds: true,
		weather: false,
		uploadedData: false,
		upload: false
	});

	// Weather settings state
	let useLivewireWeather = $state(true);
	let externalWeatherProvider = $state("openweather");
	let weatherApiKey = $state("");

	// Risk threshold settings state
	let highRiskThreshold = $state(80);
	let mediumRiskThreshold = $state(50);

	// Risk data state
	let riskData = $state({
		highRisk: 12,
		mediumRisk: 34,
		lowRisk: 156,
		geologicalRisk: 8
	});

	let weatherData = $state({
		next24h: "High",
		warning: "Storm Warning",
		expectedRain: "2.5"
	});

	let geologicalData = $state({
		landslideZones: 3,
		highSlopeAreas: 7,
		soilSaturation: "Medium"
	});

	let criticalAssets = $state([
		{
			id: "TX_001",
			name: "Transformer TX_001",
			type: "transformer",
			riskScore: 87,
			equipment: 75,
			weather: 8,
			geological: 4
		},
		{
			id: "RC_045", 
			name: "Recloser RC_045",
			type: "recloser",
			riskScore: 73,
			equipment: 60,
			weather: 10,
			geological: 3
		},
		{
			id: "P_2847",
			name: "Pole P_2847",
			type: "pole",
			riskScore: 92,
			equipment: 40,
			weather: 15,
			geological: 37,
			geologicalNote: "SLOPE"
		}
	]);

	// Uploaded data state
	let uploadedData = $state([
		{
			id: "dem_main_site",
			name: "Main Site DEM",
			type: "DEM (Digital Elevation Model)",
			fileName: "main_site_elevation_10m.tif",
			fileSize: "145 MB",
			uploadDate: "2024-08-15",
			status: "processed",
			coverage: "85%"
		},
		{
			id: "soil_classification",
			name: "Soil Classification Data",
			type: "Soil Type Data",
			fileName: "soil_types_survey_2024.shp",
			fileSize: "23 MB", 
			uploadDate: "2024-07-22",
			status: "processed",
			coverage: "92%"
		},
		{
			id: "new_survey",
			name: "Q3 Survey Data",
			type: "DEM (Digital Elevation Model)",
			fileName: "survey_q3_2024.tif",
			fileSize: "67 MB",
			uploadDate: "2024-09-01",
			status: "processing",
			coverage: "45%"
		}
	]);

	// Tab management
	const tabs = [
		{ id: "risk-overview", label: "Risk Overview", icon: faChartLine },
		{ id: "terrain-data", label: "Terrain Data", icon: faMountain },
		{ id: "reports", label: "Reports", icon: faFileText },
		{ id: "settings", label: "Settings", icon: faCog }
	];

	const setActiveTab = (tabId) => {
		activeTab = tabId;
	};

	const toggleSection = (sectionId) => {
		expandedSections[sectionId] = !expandedSections[sectionId];
	};

	// Mock data fetch
	const fetchRiskData = async () => {
		loading = true;
		error = null;
		
		try {
			// Check if user is logged in
			if (!yyyy.sesh.logtoken) {
				goto("/");
				return;
			}

			// Simulate API call delay
			await new Promise(resolve => setTimeout(resolve, 1000));
			
			// In real implementation, fetch from API
			// const response = await fetch(`${yyyy.uri.api}/rpc/getriskassessment`, {...});
			
		} catch (err) {
			console.error("Failed to fetch risk data:", err);
			error = err.message;
		} finally {
			loading = false;
		}
	};

	const refreshData = () => {
		fetchRiskData();
	};

	onMount(() => {
		// Check if user is logged in
		if (!yyyy.sesh.logtoken) {
			goto("/");
			return;
		}
		
		setTimeout(() => {
			ready = true;
		}, 400);
		
		fetchRiskData();
	});

	onDestroy(() => {
		// Cleanup if needed
	});
</script>

<div class="risk-predictor-container">
	<div class="risk-predictor-content">
		<h1 class="title thin lvwr-title" class:dim={!ready} class:bri={ready}>
			Risk Predictor
		</h1>
		<h2 class="subtitle lvwr-subtitle" class:dim={!ready} class:bri={ready}>
			Predict and prepare for electrical system risks
		</h2>

		<!-- Tab Navigation -->
		<div class="tabs-container" class:dim={!ready} class:bri={ready}>
			<div class="tabs">
				{#each tabs as tab}
					<button 
						class="tab-button"
						class:active={activeTab === tab.id}
						onclick={() => setActiveTab(tab.id)}
					>
						<Fa icon={tab.icon} />
						<span>{tab.label}</span>
					</button>
				{/each}
			</div>
		</div>

		<!-- Tab Content -->
		<div class="content lvwr-content" class:dim={!ready} class:bri={ready}>
			
			{#if activeTab === "risk-overview"}
				<div class="tab-content">
					{#if loading}
						<div class="loading-state">
							<Fa icon={faCircleNotch} spin size="2x" />
							<p>Loading risk assessment...</p>
						</div>
					{:else if error}
						<div class="error-state">
							<p>Error: {error}</p>
							<button onclick={refreshData} class="retry-button">
								<Fa icon={faRefresh} />
								Retry
							</button>
						</div>
					{:else}
						<div class="dashboard-grid">
							<!-- Risk Overview Panel -->
							<div class="dashboard-panel">
								<h3 class="panel-title">
									<Fa icon={faChartLine} />
									Risk Overview
								</h3>
								<div class="risk-stats">
									<div class="risk-stat high-risk">
										<span class="risk-number">{riskData.highRisk}</span>
										<span class="risk-label">High Risk</span>
									</div>
									<div class="risk-stat medium-risk">
										<span class="risk-number">{riskData.mediumRisk}</span>
										<span class="risk-label">Medium Risk</span>
									</div>
									<div class="risk-stat low-risk">
										<span class="risk-number">{riskData.lowRisk}</span>
										<span class="risk-label">Low Risk</span>
									</div>
									<div class="risk-stat geological-risk">
										<span class="risk-number">{riskData.geologicalRisk}</span>
										<span class="risk-label">Geological</span>
									</div>
								</div>
							</div>

							<!-- Weather Impact Panel -->
							<div class="dashboard-panel">
								<h3 class="panel-title">
									<Fa icon={faCloudRain} />
									Weather Impact
								</h3>
								<div class="weather-info">
									<div class="weather-item">
										<span class="weather-label">Next 24h:</span>
										<span class="weather-value high-severity">{weatherData.next24h}</span>
									</div>
									<div class="weather-item">
										<span class="weather-label">Alert:</span>
										<span class="weather-value">{weatherData.warning}</span>
									</div>
									<div class="weather-item">
										<span class="weather-label">Rain Expected:</span>
										<span class="weather-value">{weatherData.expectedRain}" </span>
									</div>
								</div>
							</div>

							<!-- Geological Risk Panel -->
							<div class="dashboard-panel">
								<h3 class="panel-title">
									<Fa icon={faMountain} />
									Geological Risk
								</h3>
								<div class="geological-info">
									<div class="geological-item">
										<span class="geological-label">Landslide Zones:</span>
										<span class="geological-value">{geologicalData.landslideZones}</span>
									</div>
									<div class="geological-item">
										<span class="geological-label">High Slope Areas:</span>
										<span class="geological-value">{geologicalData.highSlopeAreas}</span>
									</div>
									<div class="geological-item">
										<span class="geological-label">Soil Saturation:</span>
										<span class="geological-value">{geologicalData.soilSaturation}</span>
									</div>
								</div>
							</div>

							<!-- Critical Assets Panel -->
							<div class="dashboard-panel critical-assets-panel">
								<h3 class="panel-title">
									<Fa icon={faExclamationTriangle} />
									Critical Assets
								</h3>
								<div class="critical-assets-list">
									{#each criticalAssets as asset}
										<div class="asset-item">
											<div class="asset-header">
												<span class="asset-name">{asset.name}</span>
												<span class="risk-score" class:high-risk={asset.riskScore >= 80} class:medium-risk={asset.riskScore >= 60 && asset.riskScore < 80}>
													{asset.riskScore}%
												</span>
											</div>
											<div class="risk-breakdown">
												<div class="risk-factor">
													<span>Equipment: {asset.equipment}%</span>
													<span>Weather: {asset.weather}%</span>
												</div>
												<div class="risk-factor geological">
													<span>Geological: {asset.geological}%</span>
													{#if asset.geologicalNote}
														<span class="geological-note">({asset.geologicalNote})</span>
													{/if}
												</div>
											</div>
										</div>
									{/each}
								</div>
							</div>
						</div>
					{/if}
				</div>

			{:else if activeTab === "terrain-data"}
				<div class="tab-content">
					<div class="terrain-upload-section">
						<h3>
							<Fa icon={faUpload} />
							Terrain Data Management
						</h3>
						<p class="section-description">Upload and manage terrain data for geological risk analysis</p>
						
						<div class="upload-placeholder">
							<Fa icon={faMountain} size="3x" />
							<h4>Terrain Data Upload Interface</h4>
							<p>Upload GeoTIFF, LAS/LAZ, Shapefile formats</p>
							<p class="coming-soon">Coming Soon - Phase 2 Implementation</p>
						</div>
					</div>
				</div>

			{:else if activeTab === "settings"}
				<div class="tab-content">
					<div class="settings-section">
						<h3>
							<Fa icon={faCog} />
							Risk Predictor Settings
						</h3>
						<p class="section-description">Configure risk parameters, data uploads, and system preferences</p>
						
						<!-- Risk Threshold Settings -->
						<div class="settings-group">
							<h4 class="settings-group-title" onclick={() => toggleSection('thresholds')}>
								<span class="accordion-icon">
									<Fa icon={expandedSections.thresholds ? faChevronDown : faChevronRight} />
								</span>
								<Fa icon={faExclamationTriangle} />
								Risk Threshold Configuration
							</h4>
							{#if expandedSections.thresholds}
								<div class="settings-content" transition:slide={{ duration: 200 }}>
								<div class="setting-item">
									<label class="setting-label">Risk Score Thresholds</label>
									<div class="setting-control">
										<div class="threshold-controls">
											<div class="threshold-item">
												<label class="threshold-label">High Risk (Red):</label>
												<div class="threshold-input-group">
													<div class="field has-addons">
														<p class="control">
															<button class="button is-small" onclick={() => highRiskThreshold = Math.max(mediumRiskThreshold + 1, highRiskThreshold - 1)}>-</button>
														</p>
														<p class="control">
															<input class="input is-small has-text-centered" type="number" bind:value={highRiskThreshold} min={mediumRiskThreshold + 1} max="100" />
														</p>
														<p class="control">
															<button class="button is-small" onclick={() => highRiskThreshold = Math.min(100, highRiskThreshold + 1)}>+</button>
														</p>
													</div>
													<span class="threshold-suffix">% and above</span>
												</div>
											</div>
											<div class="threshold-item">
												<label class="threshold-label">Medium Risk (Yellow):</label>
												<div class="threshold-input-group">
													<div class="field has-addons">
														<p class="control">
															<button class="button is-small" onclick={() => mediumRiskThreshold = Math.max(1, mediumRiskThreshold - 1)}>-</button>
														</p>
														<p class="control">
															<input class="input is-small has-text-centered" type="number" bind:value={mediumRiskThreshold} min="1" max={highRiskThreshold - 1} />
														</p>
														<p class="control">
															<button class="button is-small" onclick={() => mediumRiskThreshold = Math.min(highRiskThreshold - 1, mediumRiskThreshold + 1)}>+</button>
														</p>
													</div>
													<span class="threshold-suffix">% to {highRiskThreshold - 1}%</span>
												</div>
											</div>
											<div class="threshold-item">
												<label class="threshold-label">Low Risk (Green):</label>
												<div class="threshold-static">
													<span class="threshold-value">Below {mediumRiskThreshold}%</span>
												</div>
											</div>
										</div>
									</div>
								</div>
								</div>
							{/if}
						</div>

						<!-- Weather Integration Settings -->
						<div class="settings-group">
							<h4 class="settings-group-title" onclick={() => toggleSection('weather')}>
								<span class="accordion-icon">
									<Fa icon={expandedSections.weather ? faChevronDown : faChevronRight} />
								</span>
								<Fa icon={faCloudRain} />
								Weather Integration
							</h4>
							{#if expandedSections.weather}
								<div class="settings-content" transition:slide={{ duration: 200 }}>
								<div class="setting-item">
									<label class="setting-label">Weather Data Source</label>
									<div class="setting-control">
										<div class="weather-switch-container">
											<label class="toggle-container">
												<span class="toggle-label">Use Livewire Weather</span>
												<input type="checkbox" bind:checked={useLivewireWeather} />
												<span class="toggle-switch"></span>
											</label>
											<span class="setting-help">
												{useLivewireWeather ? "Using integrated Livewire weather data" : "Use external weather API provider"}
											</span>
										</div>
									</div>
								</div>

								{#if !useLivewireWeather}
									<div class="setting-item">
										<label class="setting-label">External Provider</label>
										<div class="setting-control">
											<select class="setting-select" bind:value={externalWeatherProvider}>
												<option value="openweather">OpenWeatherMap</option>
												<option value="accuweather">AccuWeather</option>
											</select>
											<span class="setting-help">Select your preferred weather API provider</span>
										</div>
									</div>

									<div class="setting-item">
										<label class="setting-label">API Key</label>
										<div class="setting-control">
											<input 
												type="password" 
												class="api-key-input" 
												bind:value={weatherApiKey}
												placeholder="Enter your API key..."
											/>
											<span class="setting-help">
												{#if externalWeatherProvider === 'openweather'}
													Get your free API key at <a href="https://openweathermap.org/api" target="_blank">openweathermap.org</a>
												{:else if externalWeatherProvider === 'accuweather'}
													Get your API key at <a href="https://developer.accuweather.com" target="_blank">developer.accuweather.com</a>
												{/if}
											</span>
										</div>
									</div>
								{/if}
								
								<div class="setting-item">
									<label class="setting-label">Update Frequency</label>
									<div class="setting-control">
										<select class="setting-select">
											<option value="15">Every 15 minutes</option>
											<option value="30" selected>Every 30 minutes</option>
											<option value="60">Every hour</option>
											<option value="180">Every 3 hours</option>
										</select>
									</div>
								</div>
								</div>
							{/if}
						</div>

						<!-- Uploaded Data Section -->
						<div class="settings-group">
							<h4 class="settings-group-title" onclick={() => toggleSection('uploadedData')}>
								<span class="accordion-icon">
									<Fa icon={expandedSections.uploadedData ? faChevronDown : faChevronRight} />
								</span>
								<Fa icon={faEye} />
								Uploaded Data
							</h4>
							{#if expandedSections.uploadedData}
								<div class="settings-content" transition:slide={{ duration: 200 }}>
									{#if uploadedData.length > 0}
										<div class="uploaded-data-list">
											{#each uploadedData as item}
												<div class="uploaded-data-item">
													<div class="data-item-header">
														<div class="data-item-info">
															<h5 class="data-item-name">{item.name}</h5>
															<span class="data-item-type">{item.type}</span>
														</div>
														<div class="data-item-status">
															<span class="status-badge" class:processing={item.status === 'processing'} class:processed={item.status === 'processed'}>
																{item.status}
															</span>
														</div>
													</div>
													<div class="data-item-details">
														<div class="data-detail">
															<span class="detail-label">File:</span>
															<span class="detail-value">{item.fileName}</span>
														</div>
														<div class="data-detail">
															<span class="detail-label">Size:</span>
															<span class="detail-value">{item.fileSize}</span>
														</div>
														<div class="data-detail">
															<span class="detail-label">Uploaded:</span>
															<span class="detail-value">{item.uploadDate}</span>
														</div>
														<div class="data-detail">
															<span class="detail-label">Coverage:</span>
															<span class="detail-value">{item.coverage}</span>
														</div>
													</div>
													<div class="data-item-actions">
														<button class="data-action-btn view-btn">
															<Fa icon={faEye} />
															View
														</button>
														<button class="data-action-btn delete-btn">
															<Fa icon={faTrash} />
															Delete
														</button>
													</div>
												</div>
											{/each}
										</div>
									{:else}
										<div class="empty-uploaded-data">
											<p>No data has been uploaded yet.</p>
											<p class="empty-help">Upload data files to begin geological risk analysis.</p>
										</div>
									{/if}
								</div>
							{/if}
						</div>

						<!-- Data Upload Section -->
						<div class="settings-group">
							<h4 class="settings-group-title" onclick={() => toggleSection('upload')}>
								<span class="accordion-icon">
									<Fa icon={expandedSections.upload ? faChevronDown : faChevronRight} />
								</span>
								<Fa icon={faUpload} />
								Upload Data
							</h4>
							{#if expandedSections.upload}
								<div class="settings-content" transition:slide={{ duration: 200 }}>
								<div class="setting-item">
									<label class="setting-label">Select File</label>
									<div class="setting-control">
										<div class="file-upload-area">
											<input type="file" id="data-file" class="file-input" accept=".tif,.tiff,.shp,.zip" />
											<label for="data-file" class="file-upload-label">
												<Fa icon={faUpload} />
												<span>Choose file or drag and drop</span>
											</label>
											<div class="file-info">
												<span class="file-name">No file selected</span>
												<span class="file-size"></span>
											</div>
										</div>
									</div>
								</div>

								<div class="setting-item">
									<label class="setting-label">Data Type</label>
									<div class="setting-control">
										<select class="setting-select" id="data-type">
											<option value="">Select data type...</option>
											<option value="dem">DEM (Digital Elevation Model)</option>
											<option value="soil">Soil Type Data</option>
											<option value="critical-infrastructure">Critical Infrastructure</option>
											<option value="electric-load-data">Electric Load Data</option>
											<option value="vegetation-lidar">Vegetation - LIDAR</option>
										</select>
										<span class="setting-help">Specify what type of data you're uploading</span>
									</div>
								</div>

								<div class="upload-actions">
									<button class="button-upload" disabled>
										<Fa icon={faUpload} />
										Upload Data
									</button>
									<button class="button-cancel">Cancel</button>
								</div>

								<div class="setting-item">
									<label class="setting-label">Storage Usage</label>
									<div class="setting-control">
										<div class="storage-info">
											<div class="storage-bar">
												<div class="storage-used" style="width: 35%"></div>
											</div>
											<div class="storage-text">
												<span>1.2 GB used of 5 GB available</span>
												<button class="storage-manage-btn">Manage Files</button>
											</div>
										</div>
									</div>
								</div>
								</div>
							{/if}
						</div>

					</div>
				</div>

			{:else if activeTab === "reports"}
				<div class="tab-content">
					<div class="reports-section">
						<h3>
							<Fa icon={faFileText} />
							Risk Reports
						</h3>
						<p class="section-description">Generate and view risk assessment reports</p>
						
						<div class="reports-placeholder">
							<Fa icon={faFileText} size="3x" />
							<h4>Risk Assessment Reports</h4>
							<p>Historical analysis and predictive reports</p>
							<p class="coming-soon">Coming Soon - Phase 4 Implementation</p>
						</div>
					</div>
				</div>
			{/if}
		</div>

		<footer class="card-footer" class:dim={!ready} class:bri={ready}>
			<div class="card-footer-item sign-in-up-footer">
				<button
					onclick={() => goto("/livewires")}
					class="button lvwr-toggle"
				>
					Back to Livewires
				</button>
			</div>
		</footer>
	</div>
</div>

<style>
	.risk-predictor-container {
		position: fixed;
		top: 0;
		bottom: 0;
		left: 0;
		right: 0;
		margin: auto;
		width: 90%;
		max-width: 1400px;
		height: 85vh;
		padding: 0 1rem;
		z-index: 5;
	}

	.risk-predictor-content {
		background-color: rgba(255, 255, 255, 0.95);
		backdrop-filter: blur(2px);
		border: 1px solid #dbdbdb;
		border-radius: 8px;
		padding: 2rem;
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
		height: 100%;
		display: flex;
		flex-direction: column;
		overflow: hidden;
		position: relative;
	}

	.dim {
		opacity: 0%;
		transition: opacity 2.5s ease;
	}

	.bri {
		opacity: 100%;
		transition: opacity 2.5s ease;
	}

	.tabs-container {
		margin: 1rem 0;
		border-bottom: 1px solid #e0e0e0;
	}

	.tabs {
		display: flex;
		gap: 0.5rem;
	}

	.tab-button {
		display: flex;
		align-items: center;
		gap: 0.5rem;
		padding: 0.75rem 1.5rem;
		border: none;
		background: none;
		color: #666;
		cursor: pointer;
		border-radius: 4px 4px 0 0;
		transition: all 0.2s;
		font-size: 0.9rem;
		border-bottom: 3px solid transparent;
	}

	.tab-button:hover {
		background-color: rgba(255, 62, 0, 0.05);
		color: #ff3e00;
	}

	.tab-button.active {
		color: #ff3e00;
		background-color: rgba(255, 62, 0, 0.1);
		border-bottom-color: #ff3e00;
		font-weight: 500;
	}

	.content.lvwr-content {
		flex: 1;
		display: flex;
		flex-direction: column;
		min-height: 0;
		overflow-y: auto;
	}

	.tab-content {
		flex: 1;
		padding: 1rem 0;
	}

	.loading-state, .error-state {
		text-align: center;
		padding: 2rem 1rem;
		color: #666;
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 12px;
	}

	.error-state {
		color: #ff3860;
	}

	.retry-button {
		display: flex;
		align-items: center;
		gap: 0.5rem;
		padding: 0.5rem 1rem;
		border: 1px solid #3273dc;
		background-color: #3273dc;
		color: white;
		border-radius: 4px;
		cursor: pointer;
		transition: all 0.2s;
	}

	.retry-button:hover {
		background-color: #2366d1;
		border-color: #2366d1;
	}

	.dashboard-grid {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: 1.5rem;
		height: 100%;
	}

	.critical-assets-panel {
		grid-column: 1 / -1;
	}

	.dashboard-panel {
		background-color: rgba(255, 255, 255, 0.9);
		border: 1px solid #e0e0e0;
		border-radius: 6px;
		padding: 1.5rem;
		box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
	}

	.panel-title {
		display: flex;
		align-items: center;
		gap: 0.5rem;
		font-size: 1.1rem;
		font-weight: 600;
		color: #363636;
		margin-bottom: 1rem;
		padding-bottom: 0.5rem;
		border-bottom: 1px solid #f0f0f0;
	}

	.risk-stats {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: 1rem;
	}

	.risk-stat {
		display: flex;
		flex-direction: column;
		align-items: center;
		padding: 1rem;
		border-radius: 4px;
		background-color: rgba(255, 255, 255, 0.8);
		border: 1px solid #f0f0f0;
	}

	.risk-number {
		font-size: 1.8rem;
		font-weight: bold;
		line-height: 1;
	}

	.risk-label {
		font-size: 0.8rem;
		margin-top: 0.25rem;
		text-transform: uppercase;
		font-weight: 500;
	}

	.risk-stat.high-risk .risk-number {
		color: #ff3860;
	}

	.risk-stat.medium-risk .risk-number {
		color: #ffdd57;
	}

	.risk-stat.low-risk .risk-number {
		color: #48c774;
	}

	.risk-stat.geological-risk .risk-number {
		color: #8b4513;
	}

	.weather-info, .geological-info {
		display: flex;
		flex-direction: column;
		gap: 0.75rem;
	}

	.weather-item, .geological-item {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: 0.5rem;
		background-color: rgba(255, 255, 255, 0.6);
		border-radius: 3px;
	}

	.weather-label, .geological-label {
		font-weight: 500;
		color: #555;
		font-size: 0.9rem;
	}

	.weather-value, .geological-value {
		font-weight: 600;
		color: #363636;
		font-size: 0.9rem;
	}

	.weather-value.high-severity {
		color: #ff3860;
	}

	.critical-assets-list {
		display: flex;
		flex-direction: column;
		gap: 1rem;
	}

	.asset-item {
		background-color: rgba(255, 255, 255, 0.8);
		border: 1px solid #e0e0e0;
		border-radius: 4px;
		padding: 1rem;
	}

	.asset-header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: 0.5rem;
	}

	.asset-name {
		font-weight: 600;
		color: #363636;
		font-size: 0.95rem;
	}

	.risk-score {
		background-color: #3273dc;
		color: white;
		padding: 0.25rem 0.5rem;
		border-radius: 3px;
		font-weight: 600;
		font-size: 0.8rem;
	}

	.risk-score.high-risk {
		background-color: #ff3860;
	}

	.risk-score.medium-risk {
		background-color: #ffdd57;
		color: #363636;
	}

	.risk-breakdown {
		display: flex;
		flex-direction: column;
		gap: 0.25rem;
		font-size: 0.8rem;
	}

	.risk-factor {
		display: flex;
		gap: 1rem;
		color: #666;
	}

	.risk-factor.geological {
		color: #8b4513;
		font-weight: 500;
	}

	.geological-note {
		font-weight: 600;
		color: #ff3860;
	}

	.section-description {
		color: #666;
		margin-bottom: 2rem;
		font-size: 0.95rem;
	}

	.upload-placeholder, .settings-placeholder, .reports-placeholder {
		text-align: center;
		padding: 3rem 2rem;
		color: #999;
		background-color: rgba(255, 255, 255, 0.6);
		border: 2px dashed #ddd;
		border-radius: 8px;
		margin-top: 2rem;
	}

	.upload-placeholder h4, .settings-placeholder h4, .reports-placeholder h4 {
		margin: 1rem 0 0.5rem 0;
		color: #666;
		font-size: 1.2rem;
	}

	.coming-soon {
		font-style: italic;
		color: #ff3e00;
		font-weight: 500;
		margin-top: 0.5rem;
	}

	/* Settings Styles */
	.settings-group {
		background-color: rgba(255, 255, 255, 0.9);
		border: 1px solid #e0e0e0;
		border-radius: 6px;
		margin-bottom: 2rem;
		overflow: hidden;
	}

	.settings-group-title {
		display: flex;
		align-items: center;
		gap: 0.5rem;
		padding: 1rem 1.5rem;
		background-color: rgba(255, 62, 0, 0.05);
		border-bottom: 1px solid #e0e0e0;
		font-size: 1rem;
		font-weight: 600;
		color: #363636;
		margin: 0;
		cursor: pointer;
		transition: all 0.2s;
		user-select: none;
	}

	.settings-group-title:hover {
		background-color: rgba(255, 62, 0, 0.08);
	}

	.accordion-icon {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 16px;
		color: #ff3e00;
		transition: transform 0.2s;
	}

	.settings-content {
		padding: 1.5rem;
	}

	.setting-item {
		display: flex;
		align-items: flex-start;
		gap: 1rem;
		margin-bottom: 1.5rem;
		padding-bottom: 1.5rem;
		border-bottom: 1px solid #f0f0f0;
	}

	.setting-item:last-child {
		margin-bottom: 0;
		padding-bottom: 0;
		border-bottom: none;
	}

	.setting-label {
		font-weight: 500;
		color: #363636;
		min-width: 150px;
		font-size: 0.9rem;
		padding-top: 0.25rem;
	}

	.setting-control {
		flex: 1;
		display: flex;
		flex-direction: column;
		gap: 0.5rem;
	}

	.setting-select {
		padding: 0.5rem;
		border: 1px solid #dbdbdb;
		border-radius: 4px;
		font-size: 0.9rem;
		background-color: white;
		max-width: 200px;
	}

	.setting-help {
		font-size: 0.8rem;
		color: #666;
		font-style: italic;
	}

	.file-type-list {
		display: flex;
		flex-direction: column;
		gap: 0.5rem;
	}

	.file-type-item {
		display: flex;
		align-items: center;
		gap: 0.5rem;
	}

	.file-type-item input[type="checkbox"] {
		margin: 0;
	}

	.file-type-item label {
		font-size: 0.9rem;
		color: #363636;
		cursor: pointer;
	}

	.processing-options {
		display: flex;
		flex-direction: column;
		gap: 0.5rem;
	}

	.processing-item {
		display: flex;
		align-items: center;
		gap: 0.5rem;
	}

	.processing-item input[type="checkbox"] {
		margin: 0;
	}

	.processing-item label {
		font-size: 0.9rem;
		color: #363636;
		cursor: pointer;
	}

	.storage-info {
		display: flex;
		flex-direction: column;
		gap: 0.75rem;
	}

	.storage-bar {
		width: 100%;
		height: 8px;
		background-color: #f0f0f0;
		border-radius: 4px;
		overflow: hidden;
	}

	.storage-used {
		height: 100%;
		background-color: #3273dc;
		transition: width 0.3s ease;
	}

	.storage-text {
		display: flex;
		justify-content: space-between;
		align-items: center;
		font-size: 0.9rem;
		color: #666;
	}

	.storage-manage-btn {
		padding: 0.25rem 0.75rem;
		border: 1px solid #dbdbdb;
		background-color: white;
		color: #363636;
		border-radius: 3px;
		font-size: 0.8rem;
		cursor: pointer;
		transition: all 0.2s;
	}

	.storage-manage-btn:hover {
		background-color: #f5f5f5;
		border-color: #bbb;
	}

	.threshold-controls {
		display: flex;
		flex-direction: column;
		gap: 1.5rem;
	}

	.threshold-item {
		display: flex;
		align-items: center;
		gap: 1rem;
		font-size: 0.9rem;
	}

	.threshold-label {
		min-width: 140px;
		font-weight: 500;
		color: #363636;
	}

	.threshold-input-group {
		display: flex;
		align-items: center;
		gap: 0.75rem;
	}

	.threshold-suffix {
		font-size: 0.85rem;
		color: #666;
		white-space: nowrap;
	}

	.threshold-static {
		display: flex;
		align-items: center;
		min-height: 32px; /* Match the height of the input group */
	}

	.threshold-value {
		font-weight: 500;
		color: #48c774;
		font-size: 0.9rem;
	}

	/* Override Bulma field styling for threshold controls */
	.threshold-controls .field.has-addons {
		margin-bottom: 0;
	}

	.threshold-controls .input.is-small {
		width: 60px;
		min-width: 60px;
	}

	.threshold-controls .button.is-small {
		font-weight: bold;
	}

	.settings-actions {
		display: flex;
		gap: 1rem;
		justify-content: flex-start;
		padding-top: 1rem;
		border-top: 1px solid #e0e0e0;
		margin-top: 1rem;
	}

	.button-save {
		padding: 0.75rem 1.5rem;
		background-color: #48c774;
		color: white;
		border: none;
		border-radius: 4px;
		font-size: 0.9rem;
		font-weight: 500;
		cursor: pointer;
		transition: all 0.2s;
	}

	.button-save:hover {
		background-color: #3ec46d;
	}

	.button-reset {
		padding: 0.75rem 1.5rem;
		background-color: white;
		color: #363636;
		border: 1px solid #dbdbdb;
		border-radius: 4px;
		font-size: 0.9rem;
		cursor: pointer;
		transition: all 0.2s;
	}

	.button-reset:hover {
		background-color: #f5f5f5;
		border-color: #bbb;
	}

	/* File Upload Styles */
	.file-upload-area {
		display: flex;
		flex-direction: column;
		gap: 0.75rem;
	}

	.file-input {
		display: none;
	}

	.file-upload-label {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 0.5rem;
		padding: 2rem;
		border: 2px dashed #dbdbdb;
		border-radius: 6px;
		background-color: rgba(255, 255, 255, 0.8);
		cursor: pointer;
		transition: all 0.2s;
		text-align: center;
	}

	.file-upload-label:hover {
		border-color: #ff3e00;
		background-color: rgba(255, 62, 0, 0.02);
	}

	.file-upload-label span {
		color: #666;
		font-size: 0.9rem;
	}

	.file-info {
		display: flex;
		flex-direction: column;
		gap: 0.25rem;
		font-size: 0.85rem;
	}

	.file-name {
		color: #363636;
		font-weight: 500;
	}

	.file-size {
		color: #666;
	}

	.upload-actions {
		display: flex;
		gap: 1rem;
		margin: 1.5rem 0;
		padding-top: 1rem;
		border-top: 1px solid #f0f0f0;
	}

	.button-upload {
		display: flex;
		align-items: center;
		gap: 0.5rem;
		padding: 0.75rem 1.5rem;
		background-color: #ff3e00;
		color: white;
		border: none;
		border-radius: 4px;
		font-size: 0.9rem;
		font-weight: 500;
		cursor: pointer;
		transition: all 0.2s;
	}

	.button-upload:hover:not(:disabled) {
		background-color: #e63600;
	}

	.button-upload:disabled {
		background-color: #ccc;
		cursor: not-allowed;
		opacity: 0.6;
	}

	.button-cancel {
		padding: 0.75rem 1.5rem;
		background-color: white;
		color: #363636;
		border: 1px solid #dbdbdb;
		border-radius: 4px;
		font-size: 0.9rem;
		cursor: pointer;
		transition: all 0.2s;
	}

	.button-cancel:hover {
		background-color: #f5f5f5;
		border-color: #bbb;
	}

	/* Uploaded Data Styles */
	.uploaded-data-list {
		display: flex;
		flex-direction: column;
		gap: 1rem;
	}

	.uploaded-data-item {
		background-color: rgba(255, 255, 255, 0.8);
		border: 1px solid #e0e0e0;
		border-radius: 6px;
		padding: 1rem;
		transition: all 0.2s;
	}

	.uploaded-data-item:hover {
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
	}

	.data-item-header {
		display: flex;
		justify-content: space-between;
		align-items: flex-start;
		margin-bottom: 0.75rem;
	}

	.data-item-info h5 {
		margin: 0 0 0.25rem 0;
		font-size: 1rem;
		font-weight: 600;
		color: #363636;
	}

	.data-item-type {
		font-size: 0.8rem;
		color: #666;
		font-style: italic;
	}

	.status-badge {
		padding: 0.25rem 0.75rem;
		border-radius: 12px;
		font-size: 0.75rem;
		font-weight: 500;
		text-transform: uppercase;
		letter-spacing: 0.5px;
	}

	.status-badge.processed {
		background-color: #d4edda;
		color: #155724;
	}

	.status-badge.processing {
		background-color: #fff3cd;
		color: #856404;
	}

	.data-item-details {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: 0.5rem;
		margin-bottom: 1rem;
		font-size: 0.85rem;
	}

	.data-detail {
		display: flex;
		gap: 0.5rem;
	}

	.detail-label {
		font-weight: 500;
		color: #666;
		min-width: 65px;
	}

	.detail-value {
		color: #363636;
	}

	.data-item-actions {
		display: flex;
		gap: 0.75rem;
		justify-content: flex-end;
	}

	.data-action-btn {
		display: flex;
		align-items: center;
		gap: 0.5rem;
		padding: 0.5rem 1rem;
		border: 1px solid #dbdbdb;
		background-color: white;
		color: #363636;
		border-radius: 4px;
		font-size: 0.8rem;
		cursor: pointer;
		transition: all 0.2s;
	}

	.data-action-btn:hover {
		background-color: #f8f8f8;
		border-color: #bbb;
	}

	.view-btn:hover {
		background-color: #e7f3ff;
		border-color: #3273dc;
		color: #3273dc;
	}

	.delete-btn:hover {
		background-color: #ffeaea;
		border-color: #ff3860;
		color: #ff3860;
	}

	.empty-uploaded-data {
		text-align: center;
		padding: 2rem;
		color: #666;
	}

	.empty-uploaded-data p {
		margin: 0.5rem 0;
	}

	.empty-help {
		font-size: 0.9rem;
		font-style: italic;
	}

	/* Weather Integration Styles */
	.weather-switch-container {
		display: flex;
		flex-direction: column;
		gap: 0.5rem;
	}

	.toggle-container {
		display: flex;
		align-items: center;
		justify-content: space-between;
		cursor: pointer;
		user-select: none;
	}
	
	.toggle-label {
		font-weight: 500;
		color: #363636;
		flex: 1;
		font-size: 0.9rem;
	}
	
	.toggle-container input[type="checkbox"] {
		opacity: 0;
		width: 0;
		height: 0;
		position: absolute;
	}
	
	.toggle-switch {
		position: relative;
		display: inline-block;
		width: 50px;
		height: 24px;
		background-color: #ccc;
		border-radius: 4px;
		transition: background-color 0.3s;
		flex-shrink: 0;
	}
	
	.toggle-switch::after {
		content: '';
		position: absolute;
		width: 18px;
		height: 18px;
		left: 3px;
		top: 3px;
		background-color: white;
		border-radius: 2px;
		transition: transform 0.3s;
		box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
	}
	
	input[type="checkbox"]:checked + .toggle-switch {
		background-color: #ff3e00;
	}
	
	input[type="checkbox"]:checked + .toggle-switch::after {
		transform: translateX(26px);
	}
	
	input[type="checkbox"]:disabled + .toggle-switch {
		opacity: 0.5;
		cursor: not-allowed;
	}
	
	.toggle-container:hover .toggle-switch:not(:disabled) {
		box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
	}

	.api-key-input {
		width: 100%;
		max-width: 300px;
		padding: 0.5rem;
		border: 1px solid #dbdbdb;
		border-radius: 4px;
		font-size: 0.9rem;
		font-family: monospace;
		background-color: white;
		transition: border-color 0.2s;
	}

	.api-key-input:focus {
		outline: none;
		border-color: #ff3e00;
		box-shadow: 0 0 0 2px rgba(255, 62, 0, 0.1);
	}

	.api-key-input::placeholder {
		color: #999;
		font-family: inherit;
	}

	.setting-help a {
		color: #ff3e00;
		text-decoration: none;
	}

	.setting-help a:hover {
		text-decoration: underline;
	}

	@media (max-width: 768px) {
		.dashboard-grid {
			grid-template-columns: 1fr;
		}

		.risk-stats {
			grid-template-columns: 1fr 1fr;
			gap: 0.5rem;
		}

		.tab-button {
			padding: 0.5rem 1rem;
			font-size: 0.8rem;
		}

		.tab-button span {
			display: none;
		}
	}
</style>
